import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCurrency = (n) =>
  new Intl.NumberFormat('fr-CI', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 })?.format(n || 0);

const formatDate = (d) =>
  d ? new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const STATUS_LABELS = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  confirmed: { label: 'Confirmé', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  in_progress: { label: 'En cours', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  completed: { label: 'Terminé', color: 'bg-green-100 text-green-700 border-green-200' },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700 border-red-200' },
};

const DAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, sub, accent = 'brand-earth' }) => (
  <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 p-5 flex items-start space-x-4">
    <div className={`w-11 h-11 rounded-xl bg-${accent}/10 flex items-center justify-center flex-shrink-0`}>
      <Icon name={icon} size={20} className={`text-${accent}`} />
    </div>
    <div className="min-w-0">
      <p className="text-ios-caption1 text-text-secondary font-body-medium uppercase tracking-wider truncate">{label}</p>
      <p className="text-ios-title2 font-display font-display-semibold text-text-primary mt-0.5 leading-tight">{value}</p>
      {sub && <p className="text-ios-caption1 text-text-secondary mt-0.5">{sub}</p>}
    </div>
  </div>
);

const SectionHeader = ({ icon, title, subtitle, action }) => (
  <div className="flex items-center justify-between mb-5">
    <div className="flex items-center space-x-3">
      <div className="w-9 h-9 rounded-xl bg-brand-earth/10 flex items-center justify-center">
        <Icon name={icon} size={18} className="text-brand-earth" />
      </div>
      <div>
        <h2 className="text-ios-headline font-display font-display-semibold text-text-primary">{title}</h2>
        {subtitle && <p className="text-ios-caption1 text-text-secondary mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {action}
  </div>
);

// ─── Bookings Section ─────────────────────────────────────────────────────────

const BookingsSection = ({ professionalId }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updating, setUpdating] = useState(null);

  const fetchBookings = useCallback(async () => {
    if (!professionalId) return;
    setLoading(true);
    const { data, error } = await supabase?.from('professional_bookings')?.select('*')?.eq('professional_id', professionalId)?.order('preferred_date', { ascending: false });
    if (!error) setBookings(data || []);
    setLoading(false);
  }, [professionalId]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const updateStatus = async (id, status) => {
    setUpdating(id);
    await supabase?.from('professional_bookings')?.update({ booking_status: status })?.eq('id', id);
    await fetchBookings();
    setUpdating(null);
  };

  const filtered = filter === 'all' ? bookings : bookings?.filter(b => b?.booking_status === filter);

  return (
    <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 p-6">
      <SectionHeader
        icon="CalendarCheck"
        title="Réservations"
        subtitle={`${bookings?.length} réservation(s) au total`}
        action={
          <div className="flex items-center space-x-1 bg-surface rounded-xl p-1">
            {['all', 'pending', 'confirmed', 'completed']?.map(s => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-ios-caption1 font-body-medium transition-all duration-200 ${
                  filter === s ? 'bg-white shadow-ios-light text-brand-earth' : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {s === 'all' ? 'Tous' : STATUS_LABELS?.[s]?.label}
              </button>
            ))}
          </div>
        }
      />
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered?.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-14 h-14 bg-surface rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Icon name="CalendarX" size={24} className="text-text-secondary" />
          </div>
          <p className="text-ios-callout font-body-medium text-text-secondary">Aucune réservation trouvée</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered?.map(booking => {
            const st = STATUS_LABELS?.[booking?.booking_status] || STATUS_LABELS?.pending;
            return (
              <div key={booking?.id} className="flex items-start justify-between p-4 bg-surface/50 rounded-xl border border-border/30 hover:border-border/60 transition-all duration-200">
                <div className="flex items-start space-x-3 min-w-0 flex-1">
                  <div className="w-10 h-10 bg-brand-earth/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="User" size={18} className="text-brand-earth" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-ios-callout font-body-semibold text-text-primary truncate">{booking?.client_name}</p>
                    <p className="text-ios-caption1 text-text-secondary mt-0.5 capitalize">{booking?.service_type?.replace(/_/g, ' ')}</p>
                    <div className="flex items-center space-x-3 mt-1.5">
                      <span className="flex items-center space-x-1 text-ios-caption1 text-text-secondary">
                        <Icon name="Calendar" size={12} />
                        <span>{formatDate(booking?.preferred_date)}</span>
                      </span>
                      {booking?.preferred_time && (
                        <span className="flex items-center space-x-1 text-ios-caption1 text-text-secondary">
                          <Icon name="Clock" size={12} />
                          <span>{booking?.preferred_time}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-2 ml-3 flex-shrink-0">
                  <span className={`px-2.5 py-1 rounded-lg text-ios-caption2 font-body-semibold border ${st?.color}`}>
                    {st?.label}
                  </span>
                  {booking?.booking_status === 'pending' && (
                    <div className="flex items-center space-x-1.5">
                      <button
                        onClick={() => updateStatus(booking?.id, 'confirmed')}
                        disabled={updating === booking?.id}
                        className="px-2.5 py-1 bg-green-50 text-green-700 border border-green-200 rounded-lg text-ios-caption2 font-body-medium hover:bg-green-100 transition-colors disabled:opacity-50"
                      >
                        Confirmer
                      </button>
                      <button
                        onClick={() => updateStatus(booking?.id, 'cancelled')}
                        disabled={updating === booking?.id}
                        className="px-2.5 py-1 bg-red-50 text-red-700 border border-red-200 rounded-lg text-ios-caption2 font-body-medium hover:bg-red-100 transition-colors disabled:opacity-50"
                      >
                        Refuser
                      </button>
                    </div>
                  )}
                  {booking?.booking_status === 'confirmed' && (
                    <button
                      onClick={() => updateStatus(booking?.id, 'completed')}
                      disabled={updating === booking?.id}
                      className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-ios-caption2 font-body-medium hover:bg-blue-100 transition-colors disabled:opacity-50"
                    >
                      Marquer terminé
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Availability Calendar ────────────────────────────────────────────────────

const AvailabilityCalendar = ({ professional, onUpdate }) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today?.getMonth());
  const [currentYear, setCurrentYear] = useState(today?.getFullYear());
  const [bookedDates, setBookedDates] = useState([]);
  const [availableDays, setAvailableDays] = useState(professional?.available_days || []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const DAY_MAP = { lundi: 1, mardi: 2, mercredi: 3, jeudi: 4, vendredi: 5, samedi: 6, dimanche: 0 };

  useEffect(() => {
    if (!professional?.id) return;
    const fetchBooked = async () => {
      const startOfMonth = new Date(currentYear, currentMonth, 1)?.toISOString()?.split('T')?.[0];
      const endOfMonth = new Date(currentYear, currentMonth + 1, 0)?.toISOString()?.split('T')?.[0];
      const { data } = await supabase?.from('professional_bookings')?.select('preferred_date')?.eq('professional_id', professional?.id)?.in('booking_status', ['confirmed', 'in_progress'])?.gte('preferred_date', startOfMonth)?.lte('preferred_date', endOfMonth);
      setBookedDates((data || [])?.map(b => b?.preferred_date));
    };
    fetchBooked();
  }, [professional?.id, currentMonth, currentYear]);

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0)?.getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1)?.getDay();

  const isAvailableDay = (date) => {
    const dayOfWeek = date?.getDay();
    return availableDays?.some(d => DAY_MAP?.[d] === dayOfWeek);
  };

  const isBooked = (dateStr) => bookedDates?.includes(dateStr);
  const isToday = (date) => date?.toDateString() === today?.toDateString();

  const toggleDay = (dayName) => {
    setAvailableDays(prev =>
      prev?.includes(dayName) ? prev?.filter(d => d !== dayName) : [...prev, dayName]
    );
  };

  const saveAvailability = async () => {
    if (!professional?.id) return;
    setSaving(true);
    await supabase?.from('professionals')?.update({ available_days: availableDays })?.eq('id', professional?.id);
    setSaving(false);
    setSaved(true);
    onUpdate?.({ ...professional, available_days: availableDays });
    setTimeout(() => setSaved(false), 2500);
  };

  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells?.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells?.push(new Date(currentYear, currentMonth, d));

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };

  const ALL_DAYS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

  return (
    <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 p-6">
      <SectionHeader icon="CalendarDays" title="Calendrier de disponibilité" subtitle="Gérez vos jours disponibles" action={null} />
      {/* Day toggles */}
      <div className="mb-5">
        <p className="text-ios-caption1 font-body-medium text-text-secondary uppercase tracking-wider mb-2.5">Jours disponibles</p>
        <div className="flex flex-wrap gap-2">
          {ALL_DAYS?.map(day => (
            <button
              key={day}
              onClick={() => toggleDay(day)}
              className={`px-3 py-1.5 rounded-xl text-ios-caption1 font-body-medium border transition-all duration-200 capitalize ${
                availableDays?.includes(day)
                  ? 'bg-brand-earth text-white border-brand-earth shadow-ios-light'
                  : 'bg-surface text-text-secondary border-border/50 hover:border-brand-earth/50'
              }`}
            >
              {day}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-end mt-3 space-x-2">
          {saved && (
            <span className="flex items-center space-x-1.5 text-ios-caption1 text-green-600 bg-green-50 px-3 py-1.5 rounded-xl border border-green-100">
              <Icon name="CheckCircle" size={13} />
              <span>Sauvegardé</span>
            </span>
          )}
          <button
            onClick={saveAvailability}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 bg-brand-earth text-white rounded-xl text-ios-caption1 font-body-medium hover:bg-brand-earth/90 disabled:opacity-60 transition-all duration-200 shadow-ios-light"
          >
            {saving ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="Save" size={14} color="white" />}
            <span>Sauvegarder</span>
          </button>
        </div>
      </div>
      {/* Calendar grid */}
      <div className="border border-border/40 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-surface/60 border-b border-border/40">
          <button onClick={prevMonth} className="p-1.5 hover:bg-white rounded-lg transition-colors">
            <Icon name="ChevronLeft" size={16} className="text-text-secondary" />
          </button>
          <p className="text-ios-callout font-body-semibold text-text-primary">
            {MONTHS_FR?.[currentMonth]} {currentYear}
          </p>
          <button onClick={nextMonth} className="p-1.5 hover:bg-white rounded-lg transition-colors">
            <Icon name="ChevronRight" size={16} className="text-text-secondary" />
          </button>
        </div>
        <div className="grid grid-cols-7 border-b border-border/40">
          {DAYS_FR?.map(d => (
            <div key={d} className="py-2 text-center text-ios-caption2 font-body-semibold text-text-secondary uppercase tracking-wider">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells?.map((date, idx) => {
            if (!date) return <div key={`empty-${idx}`} className="h-10 border-r border-b border-border/20 last:border-r-0" />;
            const dateStr = date?.toISOString()?.split('T')?.[0];
            const available = isAvailableDay(date);
            const booked = isBooked(dateStr);
            const todayCell = isToday(date);
            return (
              <div
                key={dateStr}
                className={`h-10 flex items-center justify-center text-ios-caption1 font-body-medium border-r border-b border-border/20 last:border-r-0 transition-colors ${
                  booked ? 'bg-brand-gold/20 text-brand-earth font-body-semibold' : available ?'bg-green-50 text-green-700': 'text-text-secondary'
                } ${todayCell ? 'ring-2 ring-inset ring-brand-gold' : ''}`}
              >
                {date?.getDate()}
                {booked && <span className="ml-0.5 w-1.5 h-1.5 bg-brand-gold rounded-full inline-block" />}
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex items-center space-x-4 mt-3">
        <span className="flex items-center space-x-1.5 text-ios-caption1 text-text-secondary">
          <span className="w-3 h-3 bg-green-100 border border-green-300 rounded" />
          <span>Disponible</span>
        </span>
        <span className="flex items-center space-x-1.5 text-ios-caption1 text-text-secondary">
          <span className="w-3 h-3 bg-brand-gold/20 border border-brand-gold/40 rounded" />
          <span>Réservé</span>
        </span>
      </div>
    </div>
  );
};

// ─── Reviews Section ──────────────────────────────────────────────────────────

const ReviewsSection = ({ professionalId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aggregate, setAggregate] = useState(null);

  useEffect(() => {
    if (!professionalId) return;
    const fetchReviews = async () => {
      setLoading(true);
      const [revResult, aggResult] = await Promise.all([
        supabase?.from('professional_reviews')?.select('*')?.eq('professional_id', professionalId)?.order('created_at', { ascending: false })?.limit(20),
        supabase?.from('professional_ratings_aggregate')?.select('*')?.eq('professional_id', professionalId)?.single(),
      ]);
      const revData = revResult?.data;
      const aggData = aggResult?.data;
      setReviews(revData || []);
      setAggregate(aggData || null);
      setLoading(false);
    };
    fetchReviews();
  }, [professionalId]);

  const renderStars = (rating, size = 14) => (
    <div className="flex items-center space-x-0.5">
      {[1, 2, 3, 4, 5]?.map(s => (
        <Icon key={s} name="Star" size={size} className={s <= rating ? 'text-brand-gold fill-brand-gold' : 'text-border'} />
      ))}
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 p-6">
      <SectionHeader icon="Star" title="Avis clients" subtitle={`${reviews?.length} avis reçus`} />
      {aggregate && (
        <div className="flex items-center space-x-6 p-4 bg-surface/50 rounded-xl border border-border/30 mb-5">
          <div className="text-center">
            <p className="text-4xl font-display font-display-semibold text-brand-earth leading-none">
              {parseFloat(aggregate?.average_rating || 0)?.toFixed(1)}
            </p>
            <div className="mt-1">{renderStars(Math.round(aggregate?.average_rating || 0), 16)}</div>
            <p className="text-ios-caption1 text-text-secondary mt-1">{aggregate?.total_reviews || 0} avis</p>
          </div>
          <div className="flex-1 space-y-1.5">
            {[5, 4, 3, 2, 1]?.map(star => {
              const count = aggregate?.[`rating_${star}_count`] || 0;
              const pct = aggregate?.total_reviews > 0 ? (count / aggregate?.total_reviews) * 100 : 0;
              return (
                <div key={star} className="flex items-center space-x-2">
                  <span className="text-ios-caption2 text-text-secondary w-4 text-right">{star}</span>
                  <Icon name="Star" size={10} className="text-brand-gold fill-brand-gold flex-shrink-0" />
                  <div className="flex-1 h-1.5 bg-border/40 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-gold rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-ios-caption2 text-text-secondary w-5">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="w-7 h-7 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : reviews?.length === 0 ? (
        <div className="text-center py-10">
          <div className="w-12 h-12 bg-surface rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Icon name="MessageSquare" size={22} className="text-text-secondary" />
          </div>
          <p className="text-ios-callout font-body-medium text-text-secondary">Aucun avis pour l'instant</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
          {reviews?.map(review => (
            <div key={review?.id} className="p-4 bg-surface/40 rounded-xl border border-border/30">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-ios-callout font-body-semibold text-text-primary">{review?.reviewer_name}</p>
                  <p className="text-ios-caption1 text-text-secondary capitalize mt-0.5">{review?.service_type?.replace(/_/g, ' ')}</p>
                </div>
                <div className="flex flex-col items-end space-y-1">
                  {renderStars(review?.rating)}
                  <p className="text-ios-caption2 text-text-secondary">{formatDate(review?.created_at)}</p>
                </div>
              </div>
              {review?.feedback && (
                <p className="text-ios-callout text-text-secondary leading-relaxed italic">"{review?.feedback}"</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Portfolio Section ────────────────────────────────────────────────────────

const PortfolioSection = ({ professional, onUpdate }) => {
  const [form, setForm] = useState({
    bio: professional?.bio || '',
    specialty: professional?.specialty || '',
    tags: (professional?.tags || [])?.join(', '),
    certifications: (professional?.certifications || [])?.join(', '),
    hourlyRate: professional?.hourly_rate || professional?.hourlyRate || '',
    dailyRate: professional?.daily_rate || professional?.dailyRate || '',
    yearsExperience: professional?.years_experience || professional?.yearsExperience || '',
    websiteUrl: professional?.website_url || professional?.websiteUrl || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!professional?.id) return;
    setSaving(true);
    setError('');
    const { error: err } = await supabase?.from('professionals')?.update({
      bio: form?.bio || null,
      specialty: form?.specialty || null,
      tags: form?.tags ? form?.tags?.split(',')?.map(t => t?.trim())?.filter(Boolean) : [],
      certifications: form?.certifications ? form?.certifications?.split(',')?.map(c => c?.trim())?.filter(Boolean) : [],
      hourly_rate: form?.hourlyRate ? parseFloat(form?.hourlyRate) : null,
      daily_rate: form?.dailyRate ? parseFloat(form?.dailyRate) : null,
      years_experience: form?.yearsExperience ? parseInt(form?.yearsExperience) : 0,
      website_url: form?.websiteUrl || null,
    })?.eq('id', professional?.id);
    setSaving(false);
    if (err) { setError(err?.message); return; }
    setSaved(true);
    onUpdate?.();
    setTimeout(() => setSaved(false), 2500);
  };

  const Field = ({ label, name, type = 'text', placeholder, hint = '' }) => (
    <div>
      <label className="block text-ios-callout font-body-medium text-text-primary mb-1.5">{label}</label>
      <input
        type={type}
        value={form?.[name] || ''}
        onChange={e => setForm(f => ({ ...f, [name]: e?.target?.value }))}
        placeholder={placeholder}
        className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all duration-200"
      />
      {hint && <p className="text-ios-caption1 text-text-secondary mt-1">{hint}</p>}
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 p-6">
      <SectionHeader icon="Briefcase" title="Portfolio & Services" subtitle="Mettez à jour votre profil professionnel" />
      <div className="space-y-4">
        <div>
          <label className="block text-ios-callout font-body-medium text-text-primary mb-1.5">Bio professionnelle</label>
          <textarea
            value={form?.bio || ''}
            onChange={e => setForm(f => ({ ...f, bio: e?.target?.value }))}
            rows={4}
            placeholder="Décrivez votre expertise, vos méthodes de travail..."
            className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent resize-none transition-all duration-200"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Spécialité" name="specialty" placeholder="Ex: Rénovation intérieure" />
          <Field label="Années d'expérience" name="yearsExperience" type="number" placeholder="Ex: 5" />
          <Field label="Tarif horaire (XOF)" name="hourlyRate" type="number" placeholder="Ex: 15000" />
          <Field label="Tarif journalier (XOF)" name="dailyRate" type="number" placeholder="Ex: 80000" />
        </div>

        <Field
          label="Certifications"
          name="certifications"
          placeholder="Ex: Certification BTP, Diplôme d'architecte"
          hint="Séparez par des virgules"
        />
        <Field
          label="Tags / Compétences"
          name="tags"
          placeholder="Ex: rénovation, plomberie, électricité"
          hint="Séparez par des virgules"
        />
        <Field label="Site web" name="websiteUrl" type="url" placeholder="https://votre-site.com" />

        {error && (
          <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-xl">
            <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0" />
            <p className="text-ios-caption1 text-red-600">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-end space-x-3 pt-1">
          {saved && (
            <span className="flex items-center space-x-1.5 text-ios-caption1 text-green-600 bg-green-50 px-3 py-1.5 rounded-xl border border-green-100">
              <Icon name="CheckCircle" size={13} />
              <span>Sauvegardé</span>
            </span>
          )}
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 disabled:opacity-60 transition-all duration-200 shadow-ios-light"
          >
            {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="Save" size={15} color="white" />}
            <span>{saving ? 'Sauvegarde…' : 'Sauvegarder'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Earnings Section ─────────────────────────────────────────────────────────

const EarningsSection = ({ professionalId, professional }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    if (!professionalId) return;
    const fetch = async () => {
      setLoading(true);
      const { data } = await supabase?.from('professional_bookings')?.select('*')?.eq('professional_id', professionalId)?.eq('booking_status', 'completed')?.order('preferred_date', { ascending: false });
      setBookings(data || []);
      setLoading(false);
    };
    fetch();
  }, [professionalId]);

  const now = new Date();
  const filterByPeriod = (b) => {
    let d = new Date(b?.preferred_date);
    if (period === 'month') return d?.getMonth() === now?.getMonth() && d?.getFullYear() === now?.getFullYear();
    if (period === 'quarter') {
      const q = Math.floor(now?.getMonth() / 3);
      return Math.floor(d?.getMonth() / 3) === q && d?.getFullYear() === now?.getFullYear();
    }
    return d?.getFullYear() === now?.getFullYear();
  };

  const periodBookings = bookings?.filter(filterByPeriod);
  const dailyRate = professional?.daily_rate || professional?.dailyRate || 0;
  const hourlyRate = professional?.hourly_rate || professional?.hourlyRate || 0;
  const estimatedEarnings = periodBookings?.length * (dailyRate || hourlyRate * 8 || 0);

  // Group by month for chart
  const monthlyData = Array.from({ length: 6 }, (_, i) => {
    let d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    const count = bookings?.filter(b => {
      const bd = new Date(b?.preferred_date);
      return bd?.getMonth() === d?.getMonth() && bd?.getFullYear() === d?.getFullYear();
    })?.length;
    return { month: MONTHS_FR?.[d?.getMonth()]?.slice(0, 3), count, earnings: count * (dailyRate || hourlyRate * 8 || 0) };
  });

  const maxCount = Math.max(...monthlyData?.map(m => m?.count), 1);

  return (
    <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 p-6">
      <SectionHeader
        icon="TrendingUp"
        title="Revenus & Performance"
        subtitle="Suivi de vos missions complétées"
        action={
          <div className="flex items-center space-x-1 bg-surface rounded-xl p-1">
            {[{ key: 'month', label: 'Mois' }, { key: 'quarter', label: 'Trimestre' }, { key: 'year', label: 'Année' }]?.map(p => (
              <button
                key={p?.key}
                onClick={() => setPeriod(p?.key)}
                className={`px-3 py-1.5 rounded-lg text-ios-caption1 font-body-medium transition-all duration-200 ${
                  period === p?.key ? 'bg-white shadow-ios-light text-brand-earth' : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {p?.label}
              </button>
            ))}
          </div>
        }
      />
      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="w-7 h-7 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            <div className="p-4 bg-brand-earth/5 rounded-xl border border-brand-earth/20">
              <p className="text-ios-caption1 text-text-secondary font-body-medium">Missions terminées</p>
              <p className="text-ios-title2 font-display font-display-semibold text-brand-earth mt-1">{periodBookings?.length}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-xl border border-green-200">
              <p className="text-ios-caption1 text-text-secondary font-body-medium">Revenus estimés</p>
              <p className="text-ios-title3 font-display font-display-semibold text-green-700 mt-1 truncate">
                {estimatedEarnings > 0 ? formatCurrency(estimatedEarnings) : '—'}
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 col-span-2 sm:col-span-1">
              <p className="text-ios-caption1 text-text-secondary font-body-medium">Total missions (année)</p>
              <p className="text-ios-title2 font-display font-display-semibold text-blue-700 mt-1">{bookings?.length}</p>
            </div>
          </div>

          {/* Bar chart */}
          <div>
            <p className="text-ios-caption1 font-body-medium text-text-secondary uppercase tracking-wider mb-3">Missions / 6 derniers mois</p>
            <div className="flex items-end space-x-2 h-28">
              {monthlyData?.map((m, i) => (
                <div key={i} className="flex-1 flex flex-col items-center space-y-1">
                  <span className="text-ios-caption2 text-text-secondary font-body-medium">{m?.count > 0 ? m?.count : ''}</span>
                  <div className="w-full flex items-end justify-center" style={{ height: '72px' }}>
                    <div
                      className="w-full rounded-t-lg bg-gradient-to-t from-brand-earth to-brand-gold transition-all duration-500"
                      style={{ height: `${Math.max((m?.count / maxCount) * 100, m?.count > 0 ? 8 : 2)}%`, minHeight: '3px' }}
                    />
                  </div>
                  <span className="text-ios-caption2 text-text-secondary">{m?.month}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent completed bookings */}
          {periodBookings?.length > 0 && (
            <div className="mt-5">
              <p className="text-ios-caption1 font-body-medium text-text-secondary uppercase tracking-wider mb-3">Missions récentes</p>
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {periodBookings?.slice(0, 8)?.map(b => (
                  <div key={b?.id} className="flex items-center justify-between p-3 bg-surface/40 rounded-xl border border-border/30">
                    <div className="min-w-0">
                      <p className="text-ios-callout font-body-medium text-text-primary truncate">{b?.client_name}</p>
                      <p className="text-ios-caption1 text-text-secondary capitalize">{b?.service_type?.replace(/_/g, ' ')}</p>
                    </div>
                    <div className="text-right ml-3 flex-shrink-0">
                      <p className="text-ios-caption1 text-text-secondary">{formatDate(b?.preferred_date)}</p>
                      {(dailyRate || hourlyRate) > 0 && (
                        <p className="text-ios-caption1 font-body-semibold text-green-600">{formatCurrency(dailyRate || hourlyRate * 8)}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const ProfessionalDashboard = () => {
  const { user } = useAuth();
  const [professional, setProfessional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0, rating: 0 });

  const fetchProfessional = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    // Try to find professional by email or created_by
    const { data: profData } = await supabase?.from('professionals')?.select('*')?.or(`email.eq.${user?.email},created_by.eq.${user?.id}`)?.limit(1)?.single();

    if (profData) {
      setProfessional(profData);
      // Fetch stats
      const [bookResult, aggResult] = await Promise.all([
        supabase?.from('professional_bookings')?.select('booking_status')?.eq('professional_id', profData?.id),
        supabase?.from('professional_ratings_aggregate')?.select('average_rating, total_reviews')?.eq('professional_id', profData?.id)?.single(),
      ]);
      const bookData = bookResult?.data;
      const aggData = aggResult?.data;
      const bks = bookData || [];
      setStats({
        total: bks?.length,
        pending: bks?.filter(b => b?.booking_status === 'pending')?.length,
        completed: bks?.filter(b => b?.booking_status === 'completed')?.length,
        rating: parseFloat(aggData?.average_rating || 0)?.toFixed(1),
        totalReviews: aggData?.total_reviews || 0,
      });
    }
    setLoading(false);
  }, [user?.id, user?.email]);

  useEffect(() => { fetchProfessional(); }, [fetchProfessional]);

  const TABS = [
    { key: 'overview', label: 'Vue d\'ensemble', icon: 'LayoutDashboard' },
    { key: 'bookings', label: 'Réservations', icon: 'CalendarCheck' },
    { key: 'calendar', label: 'Calendrier', icon: 'CalendarDays' },
    { key: 'reviews', label: 'Avis', icon: 'Star' },
    { key: 'portfolio', label: 'Portfolio', icon: 'Briefcase' },
    { key: 'earnings', label: 'Revenus', icon: 'TrendingUp' },
  ];

  if (!user) {
    return (
      <div className="min-h-screen bg-surface">
        <Header />
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="w-16 h-16 bg-brand-earth/10 rounded-2xl flex items-center justify-center mb-4">
            <Icon name="Lock" size={28} className="text-brand-earth" />
          </div>
          <h2 className="text-ios-title2 font-display font-display-semibold text-text-primary mb-2">Connexion requise</h2>
          <p className="text-ios-callout text-text-secondary text-center mb-6 max-w-sm">
            Connectez-vous pour accéder à votre tableau de bord professionnel.
          </p>
          <Link to="/auth" className="px-6 py-3 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 transition-colors shadow-ios-light">
            Se connecter
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <Helmet>
        <title>Tableau de bord Professionnel — Kapital States</title>
        <meta name="description" content="Gérez vos réservations, disponibilités, avis clients, portfolio et revenus." />
      </Helmet>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center space-x-3 mb-1">
              <div className="w-10 h-10 bg-gradient-to-br from-brand-gold to-brand-earth rounded-xl flex items-center justify-center shadow-ios-light">
                <Icon name="Briefcase" size={20} color="white" />
              </div>
              <div>
                <h1 className="text-ios-title1 font-display font-display-semibold text-text-primary leading-tight">
                  Tableau de bord
                </h1>
                <p className="text-ios-callout text-text-secondary">
                  {loading ? 'Chargement…' : professional ? professional?.full_name : 'Espace Professionnel'}
                </p>
              </div>
            </div>
          </div>
          {professional && (
            <div className="hidden sm:flex items-center space-x-2">
              <span className={`px-3 py-1.5 rounded-xl text-ios-caption1 font-body-semibold border ${
                professional?.status === 'active' ? 'bg-green-50 text-green-700 border-green-200' :
                professional?.status === 'pending'? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-surface text-text-secondary border-border'
              }`}>
                {professional?.status === 'active' ? '● Actif' : professional?.status === 'pending' ? '● En attente' : professional?.status}
              </span>
              {professional?.is_verified && (
                <span className="flex items-center space-x-1 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-xl text-ios-caption1 font-body-semibold">
                  <Icon name="BadgeCheck" size={13} />
                  <span>Vérifié</span>
                </span>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-10 h-10 border-2 border-brand-gold border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-ios-callout text-text-secondary">Chargement de votre espace…</p>
          </div>
        ) : !professional ? (
          <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 p-10 text-center">
            <div className="w-16 h-16 bg-brand-earth/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="UserPlus" size={28} className="text-brand-earth" />
            </div>
            <h2 className="text-ios-title2 font-display font-display-semibold text-text-primary mb-2">
              Aucun profil professionnel trouvé
            </h2>
            <p className="text-ios-callout text-text-secondary mb-6 max-w-md mx-auto">
              Créez votre profil professionnel pour commencer à recevoir des réservations et gérer votre activité.
            </p>
            <Link
              to="/professional-signup"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 transition-colors shadow-ios-light"
            >
              <Icon name="UserPlus" size={18} color="white" />
              <span>Devenir Prestataire</span>
            </Link>
          </div>
        ) : (
          <>
            {/* Stats row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard icon="CalendarCheck" label="Total réservations" value={stats?.total} sub="Toutes périodes" />
              <StatCard icon="Clock" label="En attente" value={stats?.pending} sub="À confirmer" accent="brand-gold" />
              <StatCard icon="CheckCircle" label="Terminées" value={stats?.completed} sub="Missions complètes" />
              <StatCard icon="Star" label="Note moyenne" value={`${stats?.rating}/5`} sub={`${stats?.totalReviews} avis`} accent="brand-gold" />
            </div>

            {/* Tab navigation */}
            <div className="flex items-center space-x-1 bg-white rounded-2xl shadow-ios-light border border-border/50 p-1.5 mb-6 overflow-x-auto">
              {TABS?.map(tab => (
                <button
                  key={tab?.key}
                  onClick={() => setActiveTab(tab?.key)}
                  className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl text-ios-callout font-body-medium transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab?.key
                      ? 'bg-brand-earth text-white shadow-ios-light'
                      : 'text-text-secondary hover:text-text-primary hover:bg-surface/70'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div>
              {activeTab === 'overview' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <BookingsSection professionalId={professional?.id} />
                  <AvailabilityCalendar professional={professional} onUpdate={fetchProfessional} />
                  <ReviewsSection professionalId={professional?.id} />
                  <EarningsSection professionalId={professional?.id} professional={professional} />
                </div>
              )}
              {activeTab === 'bookings' && <BookingsSection professionalId={professional?.id} />}
              {activeTab === 'calendar' && (
                <AvailabilityCalendar professional={professional} onUpdate={fetchProfessional} />
              )}
              {activeTab === 'reviews' && <ReviewsSection professionalId={professional?.id} />}
              {activeTab === 'portfolio' && (
                <PortfolioSection professional={professional} onUpdate={fetchProfessional} />
              )}
              {activeTab === 'earnings' && (
                <EarningsSection professionalId={professional?.id} professional={professional} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfessionalDashboard;
